/**
 * A matriz de filtros da árvore.
 *
 * Duas famílias, e a diferença entre elas é o que impede a regra nº 1 de ser
 * quebrada por um filtro:
 *
 *   SELEÇÃO DE PESSOAS  franquia, cargo, profundidade. Recorta as LINHAS antes
 *                       de montar a árvore, e o totalizador passa a somar o que
 *                       sobrou. Mesma semântica do filtro de PN que já existia.
 *
 *   OCULTAÇÃO           "só com movimento" e valor mínimo. NÃO recortam linha
 *                       nenhuma: escondem nós cuja SUBÁRVORE INTEIRA não atende
 *                       ao critério, e os totais continuam exatamente os mesmos.
 *
 * Por que a segunda família não pode recortar linha: esconder um líder porque a
 * produção própria dele é zero apagaria a equipe inteira que está abaixo. E
 * recalcular o total sem ele faria a tela discordar da antiga por causa de uma
 * preferência de exibição. Escondendo só quem não tem nada na subárvore, nenhum
 * número se move e nada de verdade some da vista.
 */

const toNumber = value => {
  const number = Number(value)
  return Number.isFinite(number) ? number : 0
}

/** O estado inicial: nada filtrado. */
const emptyCriteria = () => ({
  name: '',
  franchiseIds: [],
  physicalFranchiseIds: [],
  roleIds: [],
  maxDepth: null,
  onlyWithMovement: false,
  minTotal: null,
})

/**
 * Normaliza para busca: sem acento, sem caixa.
 *
 * "Joao" tem de achar "João". Quem digita um nome numa caixa de busca não vai
 * acertar o acento, e um filtro que exige acerto exato é um filtro que as
 * pessoas param de usar.
 */
const normalizar = texto => String(texto || '')
  .normalize('NFD')
  .replace(/[̀-ͯ]/g, '')
  .toLowerCase()
  .trim()

const isEmptyCriteria = criteria => {
  const c = criteria || {}
  return !normalizar(c.name)
    && !(c.franchiseIds || []).length
    && !(c.physicalFranchiseIds || []).length
    && !(c.roleIds || []).length
    && c.maxDepth == null
    && !c.onlyWithMovement
    && (c.minTotal == null || c.minTotal === '')
}

/**
 * Linhas sem franquia: a tela antiga as descarta do total, e a nova precisa
 * descartar igual — mas dizendo quanto ficou de fora.
 *
 * `KpiList.vue:294` filtra `showUncategorized || !!item.leader_franchise_name`,
 * e `showUncategorized` só é verdadeiro com allConsultants E
 * includeInactiveFranchises E includeInactiveNetworks ligados juntos. Fora
 * disso, quem não tem franquia some do totalizador.
 *
 * Medido em junho/2026 com as flags da tela: **255 linhas sem
 * `leader_franchise_name`, 23 delas com valor, somando R$ 3.636,30 em 37
 * renovações** — todas de tamanho de assinatura, todas com `consultant_name`
 * nulo, e o mesmo consultor aparecendo em até 11 franquias. São pares
 * (consultor, franquia) que vêm do segundo passe de pagamentos via
 * contract_networks e não existem na lista de consultores.
 *
 * A regra nº 1 manda replicar: sem isto a tela nova mostraria R$ 3.636,30 a
 * mais que a antiga em junho, e o comparador de paridade acusa. Mas o dinheiro
 * não pode ficar invisível — quem chama esta função recebe o que saiu para
 * poder dizer na tela.
 *
 * MAS ISTO É REGRA DA PERSPECTIVA DE DONO, e só dela. Existe para ter paridade
 * com `KpiList.vue`, e a perspectiva de especialista não tem tela antiga com
 * que ter paridade nenhuma. Aplicá-la ali derrubava as 42 linhas de uma vez —
 * a tela mostrava R$ 0,00 e confessava "42 linhas sem franquia estão fora dos
 * totais, somando R$ 139.544,89". Achado renderizando a tela, não em teste: os
 * dois lados estavam certos isolados e a composição, errada.
 *
 * @returns {{rows: Array, excluded: Array}}
 */
const dropUncategorized = (rows, showUncategorized, perspective = 'owner') => {
  const todas = rows || []

  if (showUncategorized || perspective === 'specialist') {
    return { rows: todas, excluded: [] }
  }

  const mantidas = []
  const fora = []

  todas.forEach(row => {
    if (row.leader_franchise_name) {
      mantidas.push(row)
    } else {
      fora.push(row)
    }
  })

  return { rows: mantidas, excluded: fora }
}

/**
 * O nó da estrutura de uma linha da tabela.
 *
 * Qualquer coisa com `get(chave)` serve, não só Map: a tela passa o índice de
 * `indexStructure` embrulhado num `{ get }`. Testar `instanceof Map` fazia esse
 * objeto cair no acesso por propriedade, que devolve sempre undefined — e o
 * recorte por unidade física, cargo e nível esvaziava a tabela.
 */
const noDaLinha = (structureByKey, row) => {
  const chave = `${toNumber(row.consultant_id || row.id)}:${toNumber(row.digital_franchise_id)}`
  return structureByKey && typeof structureByKey.get === 'function'
    ? structureByKey.get(chave)
    : (structureByKey || {})[chave]
}

/**
 * Recorta as linhas por atributo de PESSOA, antes da árvore ser montada.
 *
 * Quem sobrar sem líder no recorte não some: buildTree o pendura no ancestral
 * mais próximo que ficou, usando o caminho que o backend manda.
 *
 * @param {Array} rows linhas de /table
 * @param {Map|Object} structureByKey nós de /structure, por "consultor:franquia"
 * @param {Object} criteria
 */
const filterRows = (rows, structureByKey, criteria) => {
  const c = { ...emptyCriteria(), ...(criteria || {}) }
  const franchises = new Set((c.franchiseIds || []).map(Number))
  const unidades = new Set((c.physicalFranchiseIds || []).map(Number))
  const roles = new Set((c.roleIds || []).map(Number))
  const nome = normalizar(c.name)

  if (!franchises.size && !unidades.size && !roles.size && c.maxDepth == null && !nome) {
    return rows || []
  }

  return (rows || []).filter(row => {
    const franchiseId = toNumber(row.digital_franchise_id)

    if (franchises.size && !franchises.has(franchiseId)) {
      return false
    }

    if (nome && !normalizar(row.name || row.consultant_name).includes(nome)) {
      return false
    }

    const node = noDaLinha(structureByKey, row)

    if (unidades.size) {
      // A unidade física vem da estrutura, não da linha da tabela. Sem
      // estrutura não há unidade, e manter quem não tem mentiria sobre o
      // recorte — a mesma regra do cargo.
      if (!node || !unidades.has(toNumber(node.physical_franchise_id))) {
        return false
      }
    }

    if (roles.size) {
      // Sem estrutura não há cargo. Filtrar por cargo e manter quem não tem
      // seria mentir sobre o recorte, então ele sai.
      if (!node || !roles.has(toNumber(node.consultant_role_id))) {
        return false
      }
    }

    if (c.maxDepth != null) {
      // Sem estrutura a profundidade é 0 — o consultor é raiz da própria vista.
      const depth = node ? toNumber(node.depth) : 0
      if (depth > Number(c.maxDepth)) {
        return false
      }
    }

    return true
  })
}

/**
 * Quem esconder DEPOIS da árvore montada, sem tocar em nenhum total.
 *
 * Um nó só entra aqui se a subárvore inteira dele reprovar. Assim nada de
 * verdade fica escondido atrás de um pai oculto, e o subtotal que continua na
 * tela segue sendo o mesmo de antes.
 *
 * @returns {Set<string>} chaves a esconder
 */
const hiddenKeys = (roots, criteria) => {
  const c = { ...emptyCriteria(), ...(criteria || {}) }
  const hidden = new Set()

  if (!c.onlyWithMovement && (c.minTotal == null || c.minTotal === '')) {
    return hidden
  }

  const minimo = c.minTotal == null || c.minTotal === '' ? 0 : Number(c.minTotal)

  const reprova = node => {
    const total = toNumber(node.totals.total_contract_amount)
    if (c.onlyWithMovement && total <= 0) {
      return true
    }
    return total < minimo
  }

  const stack = [...(roots || [])]
  while (stack.length) {
    const node = stack.pop()
    if (reprova(node)) {
      // A subárvore inteira reprova junto: os totais dela estão contidos neste,
      // então nenhum descendente pode passar onde o ancestral não passou — e
      // por isso não se desce mais por aqui.
      hidden.add(node.key)
    } else {
      node.children.forEach(child => stack.push(child))
    }
  }

  return hidden
}

/**
 * As opções que a tela oferece, montadas do que veio — não de uma lista fixa.
 *
 * Uma lista fixa envelhece: franquia nova aparece no dado e não no filtro, e
 * ninguém descobre até alguém reclamar que "sumiu".
 *
 * Com `structureByKey`, cargo e unidade saem dos nós que as LINHAS ocupam, e
 * não da estrutura inteira. A estrutura traz a rede toda, com quem não fatura
 * no período: medido em junho/2026, 1.979 nós para 167 linhas, e 5 das 12
 * unidades oferecidas (São Paulo com 437 nós) não tinham linha nenhuma —
 * escolher uma delas dava "Nenhum consultor", e parecia filtro quebrado.
 * O que já está escolhido continua na lista, senão o select mostraria o id.
 *
 * @param {Object} [contexto] `{ structureByKey, criteria }`
 */
const availableFilters = (rows, structureNodes, roles, physicalFranchises, contexto = {}) => {
  const franquias = new Map()
  const linhas = rows || []
  linhas.forEach(row => {
    const id = toNumber(row.digital_franchise_id)
    if (id && !franquias.has(id)) {
      franquias.set(id, row.leader_franchise_name || `Franquia ${id}`)
    }
  })

  const cargosEmUso = new Set()
  const unidadesEmUso = new Set()
  let maiorProfundidade = 0
  const nos = structureNodes || []
  nos.forEach(node => {
    maiorProfundidade = Math.max(maiorProfundidade, toNumber(node.depth))
  })

  const { structureByKey, criteria } = contexto || {}
  const nosEmUso = structureByKey
    ? linhas.map(row => noDaLinha(structureByKey, row)).filter(Boolean)
    : nos
  nosEmUso.forEach(node => {
    if (node.consultant_role_id) {
      cargosEmUso.add(toNumber(node.consultant_role_id))
    }
    if (node.physical_franchise_id) {
      unidadesEmUso.add(toNumber(node.physical_franchise_id))
    }
  })
  const escolhidos = criteria || {}
  const escolhidosCargos = escolhidos.roleIds || []
  const escolhidasUnidades = escolhidos.physicalFranchiseIds || []
  escolhidosCargos.forEach(id => cargosEmUso.add(toNumber(id)))
  escolhidasUnidades.forEach(id => unidadesEmUso.add(toNumber(id)))

  return {
    franchises: [...franquias.entries()]
      .map(([id, name]) => ({ id, name }))
      .sort((first, second) => String(first.name).localeCompare(String(second.name), 'pt-BR')),
    // Só os cargos que existem no recorte: oferecer os 25 quando o usuário
    // enxerga 3 transforma o filtro num campo minado de resultados vazios.
    roles: (roles || [])
      .filter(role => cargosEmUso.has(toNumber(role.id)))
      .map(role => ({ id: toNumber(role.id), name: role.name })),
    physicalFranchises: (physicalFranchises || [])
      .filter(unidade => unidadesEmUso.has(toNumber(unidade.id)))
      .map(unidade => ({ id: toNumber(unidade.id), name: unidade.name })),
    maxDepth: maiorProfundidade,
  }
}

module.exports = {
  availableFilters,
  dropUncategorized,
  normalizar,
  emptyCriteria,
  filterRows,
  hiddenKeys,
  isEmptyCriteria,
}
